/**
 * The platform's AI, called directly.
 *
 * Verawood's only AI feature is an out-of-tree Django App Plugin, `openedx-ai-extensions`,
 * and this file is our whole client for it. Four endpoints, mounted by the plugin at
 * `/openedx-ai-extensions/v1/` in BOTH the LMS and Studio:
 *
 *   POST   workflows/           run a workflow. Streams `text/plain`, or answers JSON.
 *   GET    profile/             the ONE workflow that applies to a context, and its UI.
 *   GET    profiles/            every workflow reachable from a context. Course staff only.
 *   GET    prompts/{slug}/      a prompt template's body. Course staff only.
 *
 * WE INSTALLED THE BACKEND ONLY, AND THAT IS WHY THIS FILE EXISTS. Upstream ships its UI as
 * `@openedx/openedx-ai-extensions-ui`, a Frontend Plugin Framework package that renders into
 * MFE slots. We run no MFEs, so it has no host to render into. What it does have is a good
 * idea, and this file keeps it: the SERVER decides which AI exists and what it is called.
 *
 * THE CONTRACT IS A DATABASE ROW, NOT A DEPLOY. `GET profile/` matches an `AIWorkflowScope`
 * on three fields — `ui_slot_selector_id`, an optional `course_id`, an optional
 * `location_regex` — weighted for specificity (location +4, course +2, slot +1), and returns
 * the winning profile's `ui_components`: which component to render, its button text, its
 * heading. So an academy gains an AI feature by inserting two rows, and this app ships
 * unchanged. That is the same principle as tenants-as-data everywhere else here.
 *
 * NOTHING IS HARDCODED ABOUT WHICH WORKFLOWS EXIST. A slot with no scope row answers
 * `{"status": "no_config"}` — a value, not an error — and the caller renders nothing. An
 * academy that has not switched AI on sees no button rather than a broken one.
 */

import { getConfig } from './config';
import { ApiError, openedx, requestStream } from './openedx';

const BASE = '/openedx-ai-extensions/v1';

/* ------------------------------------------------------------------- context ----- */

export interface AiContext {
  /**
   * Must equal an `AIWorkflowScope.ui_slot_selector_id` row or NOTHING resolves.
   *
   * Required, and required in a way worth stating: `AIWorkflowScope.get_profile` returns
   * None the moment this is absent, so an omitted slot is not "match any" — it is "match
   * nothing". A typo here looks exactly like AI being switched off.
   */
  slot: string;
  /** Course key. Validated server-side as an opaque CourseKey; a malformed one is a 400. */
  courseId?: string;
  /** The vertical's usage key. Validated server-side as an opaque UsageKey. */
  locationId?: string;
}

/**
 * Context travels as ONE json-encoded query parameter, on GET and POST alike.
 *
 * Not as ordinary query fields, and not in the POST body — `get_context_from_request` reads
 * `request.GET["context"]` and json-decodes it, for every endpoint here. A POST therefore
 * carries its context in the URL and its input in the body, which reads oddly and is simply
 * what the server does.
 *
 * camelCase because the server accepts `courseId` OR `course_id` and normalises internally;
 * camelCase is what upstream's own client sends, so it is the better-tested path.
 */
function contextQuery(ctx: AiContext): string {
  const payload: Record<string, string> = { uiSlotSelectorId: ctx.slot };
  if (ctx.courseId) payload.courseId = ctx.courseId;
  if (ctx.locationId) payload.locationId = ctx.locationId;
  return `?context=${encodeURIComponent(JSON.stringify(payload))}`;
}

/* ------------------------------------------------------------------ profiles ----- */

/** One half of a workflow's UI, as the server names it. */
export interface AiUiComponent {
  /**
   * A component NAME, resolved by the client — 'AIRequestComponent', 'AISidebarResponse'.
   *
   * These are upstream's React component names, and they are the vocabulary the database
   * speaks whether or not upstream's React is present. We map the names we implement and
   * ignore the rest, so a profile configured for a component we do not have renders nothing
   * instead of throwing.
   */
  component: string;
  /** Free-form strings from the profile: `buttonText`, `customMessage`, and others. */
  config: Record<string, string>;
}

export interface AiProfile {
  course_id: string | null;
  ui_components: {
    request?: AiUiComponent;
    response?: AiUiComponent;
  };
  timestamp: string;
}

interface NoConfig {
  status: 'no_config';
  timestamp: string;
}

/**
 * The workflow for one context, or null when there is none.
 *
 * Null covers both "no scope row matches" (the server's own `no_config`) and every
 * failure, deliberately: an AI button is an enhancement, and no enhancement is a better
 * outcome than an error banner about an enhancement. Real failures still reach the console
 * through the caller's query state.
 */
export async function getAiProfile(ctx: AiContext): Promise<AiProfile | null> {
  const data = await openedx.get<AiProfile | NoConfig>(`${BASE}/profile/${contextQuery(ctx)}`);
  if (!data || (data as NoConfig).status === 'no_config') return null;
  return data as AiProfile;
}

export interface AiScopeSummary {
  id: string;
  course_id: string | null;
  service_variant: string;
  enabled: boolean;
  ui_slot_selector_id: string;
  location_regex: string | null;
  specificity_index: number;
}

/**
 * Every workflow reachable from a context — the admin view of what AI this academy has.
 *
 * COURSE STAFF ONLY (`CourseStaffPermission`), so a learner gets 403 and this must never be
 * called from a learner surface. Omit `slot` semantics by passing a slot the server ignores:
 * with no `uiSlotSelectorId` the server returns profiles for ALL slots, which is the
 * intended call for a settings panel.
 */
export async function listAiProfiles(
  ctx: Omit<AiContext, 'slot'> & { slot?: string },
): Promise<{ profiles: unknown[]; count: number }> {
  const payload: Record<string, string> = {};
  if (ctx.slot) payload.uiSlotSelectorId = ctx.slot;
  if (ctx.courseId) payload.courseId = ctx.courseId;
  if (ctx.locationId) payload.locationId = ctx.locationId;
  const query = `?context=${encodeURIComponent(JSON.stringify(payload))}`;
  return openedx.get(`${BASE}/profiles/${query}`);
}

/* ------------------------------------------------------------------- running ----- */

/**
 * The actions a workflow can be asked to perform.
 *
 * Dispatched by NAME onto the profile's orchestrator: the view does
 * `getattr(orchestrator, action)(user_input)`, so an action an orchestrator does not
 * implement is a 500, not a 400. `DirectLLMResponse` — what a summary profile uses —
 * implements `run` and `get_current_session_response` and nothing else.
 */
export const AI_ACTIONS = {
  RUN: 'run',
  RUN_ASYNC: 'run_async',
  GET_CURRENT_SESSION_RESPONSE: 'get_current_session_response',
  GET_RUN_STATUS: 'get_run_status',
  CANCEL_RUN: 'cancel_run',
  CLEAR_SESSION: 'clear_session',
  LAZY_LOAD_CHAT_HISTORY: 'lazy_load_chat_history',
  REGENERATE_QUESTION: 'regenerate_question',
  SAVE: 'save',
} as const;

export type AiAction = (typeof AI_ACTIONS)[keyof typeof AI_ACTIONS];

/** A failure the AI service described in its own words, carrying its own code. */
export class AiError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message || code);
    this.name = 'AiError';
  }
}

/**
 * An error can arrive AFTER a 200 and mid-sentence, and it arrives in-band.
 *
 * The server has already committed to a streaming 200 by the time the model fails, so it
 * cannot change the status code. It splices a marker into the text instead:
 *
 *     …the first half of an answer||{"code":"context_window_exceeded","message":"…"}||
 *
 * Which means a client that just concatenates chunks prints that JSON to the learner as
 * though the model wrote it. Everything before the marker is real output and is kept;
 * everything from the marker on is a failure and is raised.
 */
const STREAM_ERROR_MARKER = '||{"error_in_stream":';

/**
 * Run a workflow and return its text.
 *
 * `onChunk` fires as tokens arrive for a streaming profile and never fires for a
 * non-streaming one — the resolved string is complete either way, so a caller that only
 * wants the answer can ignore it entirely.
 */
export async function runWorkflow(
  ctx: AiContext,
  input: { action?: AiAction; userInput?: string | Record<string, unknown> } = {},
  handlers: { onChunk?: (chunk: string) => void; signal?: AbortSignal } = {},
): Promise<string> {
  // snake_case: the view reads `request.data["action"]` and `request.data["user_input"]`.
  // Nothing else in the body is read — upstream's client also sends a request id and a
  // timestamp, and the server ignores both, so they are omitted rather than faked.
  const body = {
    action: input.action ?? AI_ACTIONS.RUN,
    user_input: input.userInput ?? '',
  };

  let seen = '';
  let forwarded = 0;

  const result = await requestStream(
    `${BASE}/workflows/${contextQuery(ctx)}`,
    {
      json: body,
      signal: handlers.signal,
      onChunk: (chunk) => {
        seen += chunk;
        const marker = seen.indexOf(STREAM_ERROR_MARKER);
        const safeEnd = marker === -1 ? seen.length : marker;

        // Forward only what is known not to be part of a marker, and only once. Slicing
        // from `forwarded` rather than passing `chunk` through is what makes a marker split
        // across two network chunks safe: the first half is held back, not printed.
        if (safeEnd > forwarded) {
          handlers.onChunk?.(seen.slice(forwarded, safeEnd));
          forwarded = safeEnd;
        }

        if (marker === -1) return;

        // Wait for the closing `||` before parsing: a truncated marker is not yet an error,
        // it is an error arriving.
        const end = seen.indexOf('||', marker + 2);
        if (end === -1) return;

        let code = 'streaming_failed';
        let message = '';
        try {
          const parsed = JSON.parse(seen.slice(marker + 2, end)) as {
            code?: string;
            message?: string;
          };
          code = parsed.code ?? code;
          message = parsed.message ?? '';
        } catch {
          /* the marker itself was malformed; the generic code is still right */
        }
        throw new AiError(code, message);
      },
    },
  );

  if (result.json) {
    const payload = result.json as { response?: string; status?: string; error?: unknown };
    if (payload.status === 'error' || payload.error) {
      throw new AiError('processor_error', '');
    }
    return payload.response ?? '';
  }

  return seen.slice(0, forwarded);
}

/* ------------------------------------------------------------------ failures ----- */

/**
 * What to tell the person in front of us.
 *
 * Written for a learner, not an operator, and it never repeats the server's own message:
 * those are written for logs and quote the provider verbatim. Measured on this install —
 * an OpenAI account with no credits produces a provider `429 insufficient_quota`, which
 * surfaces here as `processor_error`. That is an ACADEMY billing problem presented to
 * someone trying to read a unit, so the copy has to be honest about there being nothing
 * for them to fix and clear that their work is untouched.
 */
const FAILURE_COPY: Record<string, string> = {
  invalid_api_key:
    'The academy’s AI service is not configured correctly. Nothing you did caused this — tell the course team.',
  rate_limit_exceeded:
    'The AI service is busy right now. Wait a moment and try again.',
  service_unavailable:
    'The AI service is unavailable right now. It is worth trying again shortly.',
  context_window_exceeded:
    'This unit is too long for the AI to read in one go. Try it on a shorter unit.',
  validation_error:
    'The AI could not be asked about this unit. Tell the course team if it keeps happening.',
  streaming_failed:
    'The answer stopped part way through. What you can see above is real; ask again for the rest.',
  processor_error:
    'The AI service could not answer. This is an academy setup problem, not something you did — tell the course team.',
  internal_error:
    'Something went wrong on the academy’s side. Try again, and tell the course team if it persists.',
};

const GENERIC =
  'The AI could not answer just now. Nothing has changed in your course — try again shortly.';

export function explainAiFailure(error: unknown): string {
  if (error instanceof AiError) return FAILURE_COPY[error.code] ?? GENERIC;

  if (error instanceof ApiError) {
    // 401 has already been through one refresh-and-retry inside the client by the time it
    // reaches here, so it means signed out rather than a token blip.
    if (error.status === 401) return 'Your session ended. Sign in again to use this.';
    if (error.status === 403) return 'This academy has not made AI available to your account.';
    const code = (error.body as { error?: { code?: string } } | null)?.error?.code;
    if (code) return FAILURE_COPY[code] ?? GENERIC;
  }

  return GENERIC;
}

/**
 * Has this academy switched AI off outright?
 *
 * OPT-OUT, not opt-in, and the asymmetry is deliberate. What AI exists is already decided
 * by scope rows on the server, so a second opt-in flag here would mean two switches for one
 * decision and a support call every time only one of them is on. This exists for the other
 * case: an academy that wants no AI at all, whose learners should not even generate the
 * profile request.
 */
export function aiDisabledForTenant(): boolean {
  return getConfig().features.ai === false;
}
